import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { AuthenticationService } from '../../modules/core/authentication/authentication-service.service';
import { GlobalToastService } from '../global-toast/global-toast.service';
import { getTokenExpirationState } from '../token/token.utils';
import { environment } from '../../../../environments/environment';
import { ApiResponse } from '../../../shared/models/api-response.model';
import { LoginResponse } from '../../v2/models/user.models';

export type SessionDialogMode = 'refresh' | 'relogin';

@Injectable({ providedIn: 'root' })
export class SessionService {
    // #region Dependency Injection
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly toastService = inject(GlobalToastService);
    // #endregion

    // #region Configuration
    private readonly baseUrl = `${environment.baseUrl}/api/v2/auth`;
    // #endregion

    // #region Signals & State
    readonly dialogVisible = signal(false);
    readonly dialogMode = signal<SessionDialogMode>('refresh');
    readonly sessionEmail = signal<string>('');
    readonly isProcessing = signal(false);
    readonly errorMessage = signal<string | null>(null);
    // #endregion

    // #region Private State
    private pendingResolve: ((success: boolean) => void) | null = null;
    // #endregion

    // #region Public Methods

    /**
     * Check the current session's token state and show dialog if expired.
     * Returns 'valid' if no action needed, or a Promise<boolean> that resolves
     * when the dialog flow completes (true = session restored, false = logged out).
     */
    checkSession(): 'valid' | Promise<boolean> {
        const session = this.authenticationService.getUserSession();
        if (!session?.token) {
            return Promise.resolve(false);
        }

        const state = getTokenExpirationState(session.token);
        if (state === 'valid') {
            return 'valid';
        }

        // Don't open multiple dialogs — share the pending promise
        if (this.dialogVisible()) {
            return new Promise(resolve => {
                const existingResolve = this.pendingResolve;
                this.pendingResolve = (success: boolean) => {
                    existingResolve?.(success);
                    resolve(success);
                };
            });
        }

        this.sessionEmail.set(session.email);
        this.errorMessage.set(null);
        this.dialogMode.set(state === 'grace-period' ? 'refresh' : 'relogin');
        this.dialogVisible.set(true);

        return new Promise(resolve => {
            this.pendingResolve = resolve;
        });
    }

    /**
     * Called from the dialog: silently refresh the token (grace period mode).
     */
    async refreshSession(): Promise<void> {
        const session = this.authenticationService.getUserSession();
        if (!session?.token) {
            this.onFailure();
            return;
        }

        this.isProcessing.set(true);
        this.errorMessage.set(null);

        try {
            const response = await firstValueFrom(
                this.http.post<ApiResponse<LoginResponse>>(`${this.baseUrl}/refresh-token`, {
                    token: session.token,
                })
            );

            if (response?.success && response.data) {
                this.updateSession(response.data);
                this.onSuccess('Session refreshed');
            } else {
                // Grace period may have passed while dialog was open; switch to relogin
                this.dialogMode.set('relogin');
                this.errorMessage.set('Session could not be refreshed. Please enter your password.');
            }
        } catch {
            this.dialogMode.set('relogin');
            this.errorMessage.set('Session could not be refreshed. Please enter your password.');
        } finally {
            this.isProcessing.set(false);
        }
    }

    /**
     * Called from the dialog: re-authenticate with password (expired mode).
     */
    async reloginWithPassword(password: string): Promise<void> {
        const email = this.sessionEmail();
        if (!email) {
            this.onFailure();
            return;
        }

        this.isProcessing.set(true);
        this.errorMessage.set(null);

        try {
            const response = await firstValueFrom(
                this.http.post<ApiResponse<LoginResponse>>(`${this.baseUrl}/login`, {
                    email,
                    password,
                })
            );

            if (response?.success && response.data) {
                this.updateSession(response.data);
                this.onSuccess('Session restored');
            } else {
                this.errorMessage.set(response?.error ?? 'Invalid password. Please try again.');
            }
        } catch {
            this.errorMessage.set('Authentication failed. Please try again.');
        } finally {
            this.isProcessing.set(false);
        }
    }

    /**
     * Reset all dialog state — call this on a fresh login to clear any stale session-expired state.
     */
    reset(): void {
        this.dialogVisible.set(false);
        this.dialogMode.set('refresh');
        this.sessionEmail.set('');
        this.isProcessing.set(false);
        this.errorMessage.set(null);
        this.pendingResolve = null;
    }

    /**
     * DEV ONLY — rewrite the stored token's `exp` claim into the past and open
     * the session dialog, so the expiry flow can be exercised on demand without
     * waiting ~40 min. No-op in a production build. Wired to a dev-only item in
     * the admin-v3 topbar user menu.
     *
     * @param mode `'relogin'` (default) backdates past the grace period →
     *   password re-entry; `'refresh'` lands inside the grace window → silent
     *   refresh button.
     */
    expireForTest(mode: SessionDialogMode = 'relogin'): void {
        if (environment.production) return;

        const session = this.authenticationService.getUserSession();
        if (!session?.token) return;

        const [header, payload, signature] = session.token.split('.');
        let claims: Record<string, unknown>;
        try {
            claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        } catch {
            return;
        }

        claims['exp'] = Math.floor(Date.now() / 1000) - (mode === 'refresh' ? 60 : 3600);
        const encoded = btoa(JSON.stringify(claims)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

        this.authenticationService.setUserSession({ ...session, token: `${header}.${encoded}.${signature}` });
        this.checkSession();
    }

    /**
     * Called if user clicks "Log out" on the dialog.
     */
    logout(): void {
        this.dialogVisible.set(false);
        localStorage.clear();
        this.pendingResolve?.(false);
        this.pendingResolve = null;
        this.router.navigate(['/login']);
    }

    // #endregion

    // #region Private Methods

    private updateSession(loginData: LoginResponse): void {
        const updatedSession = {
            userId: loginData.user.userId,
            firstName: loginData.user.firstName,
            lastName: loginData.user.lastName,
            email: loginData.user.email,
            role: loginData.user.role,
            isActive: loginData.user.isActive,
            accountStatus: 'Active',
            emailVerified: true,
            accountId: loginData.currentAccount?.accountId,
            accountRole: loginData.currentAccount?.role,
            token: loginData.token,
        };
        this.authenticationService.setUserSession(updatedSession as any);
    }

    private onSuccess(message: string): void {
        this.dialogVisible.set(false);
        this.toastService.success(message);
        this.pendingResolve?.(true);
        this.pendingResolve = null;
    }

    private onFailure(): void {
        this.dialogVisible.set(false);
        localStorage.clear();
        this.pendingResolve?.(false);
        this.pendingResolve = null;
        this.router.navigate(['/login']);
    }

    // #endregion
}
